import { useState, useEffect, useRef, useCallback } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { calculateTotals } from '../lib/utils';

/**
 * Hook pour l'auto-sauvegarde des documents (devis/factures)
 * @param {Object} params - Paramètres
 * @param {Object} params.user - Utilisateur connecté
 * @param {string} params.activeCompanyId - ID de l'entreprise active
 * @param {string} params.type - Type de document ('quote' ou 'invoice')
 * @param {Object} params.docInfo - Informations du document
 * @param {Array} params.sections - Sections du document
 * @param {Object} params.globalSettings - Paramètres globaux (TVA, remise)
 * @param {Object} params.client - Client sélectionné
 * @param {number} params.interval - Intervalle d'auto-save en ms (défaut: 10000)
 * @returns {Object} - { isSaving, lastAutoSave, triggerSave }
 */
export function useAutoSave({
  user,
  activeCompanyId,
  type,
  docInfo,
  sections,
  globalSettings,
  client,
  interval = 10000
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState(null);

  // Ref pour accéder aux données les plus récentes dans l'intervalle
  const dataRef = useRef({ docInfo, sections, globalSettings, client });

  // Mettre à jour la ref quand les données changent
  useEffect(() => {
    dataRef.current = { docInfo, sections, globalSettings, client };
  }, [docInfo, sections, globalSettings, client]);

  // Fonction de sauvegarde
  const saveDocument = useCallback(async () => {
    const currentData = dataRef.current;
    if (!user || !activeCompanyId || !currentData.docInfo.id) return false;

    try {
      setIsSaving(true);
      const totals = calculateTotals(currentData.sections, currentData.globalSettings);
      const collectionName = type === 'quote' ? 'quotes' : 'invoices';

      const dataToSave = Object.fromEntries(
        Object.entries({
          ...currentData.docInfo,
          clientName: currentData.client.name || '',
          clientId: currentData.client.id || null,
          total: totals.totalTTC,
          sections: currentData.sections,
          globalSettings: currentData.globalSettings
        }).filter(([_, value]) => value !== undefined)
      );

      await setDoc(
        doc(db, 'companies', activeCompanyId, collectionName, String(currentData.docInfo.id)),
        dataToSave
      );

      setLastAutoSave(new Date());
      setIsSaving(false);
      return true;
    } catch (error) {
      console.error('Erreur auto-save:', error);
      setIsSaving(false);
      return false;
    }
  }, [user, activeCompanyId, type]);

  // Auto-save avec intervalle
  useEffect(() => {
    if (!user || !activeCompanyId || !docInfo.id) return;

    const autoSaveInterval = setInterval(() => {
      saveDocument();
    }, interval);

    return () => {
      clearInterval(autoSaveInterval);
    };
  }, [user, activeCompanyId, docInfo.id, interval, saveDocument]);

  // Fonction pour déclencher une sauvegarde manuelle
  const triggerSave = useCallback(async () => {
    return await saveDocument();
  }, [saveDocument]);

  return {
    isSaving,
    setIsSaving,
    lastAutoSave,
    setLastAutoSave,
    triggerSave
  };
}

export default useAutoSave;
